import React, { useMemo } from 'react';
import styled from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';

import type { IBlockSettings, ISortedGroupedSchedule, ISortedShift } from '@/types/schedule';

import { formatStartEndTime, lineClamp } from '@/helpers';
import { useAppSelector, useTimezone } from '@/hooks';
import { HALF_AN_HOUR_BLOCK_SPAN, HOUR_IN_PX } from '@/config/variables';
import { selectSchedulesList } from '@/store/selectors/scheduleSelectors';
import { getDateFromBlock } from '@/helpers/block';

interface IProps {
  contract: ISortedShift<ISortedGroupedSchedule>;
  blockSettings: IBlockSettings;
  blockColor: string;
}

const schedulesContainerLabelsSelector = createSelector(selectSchedulesList, schedulesList => ({ schedulesList }));

const SchedulesContainerLabels = ({ contract, blockSettings, blockColor }: IProps): React.ReactElement => {
  const { schedulesList } = useAppSelector(schedulesContainerLabelsSelector);
  const { timezoneName } = useTimezone();
  const { clientName, scheduleName, scheduleNameShort } = contract;

  const scheduleIndex = schedulesList.findIndex(schedule => schedule.scheduleId === contract.scheduleId) + 1;
  const isShort = !(blockSettings.blockDuration > HOUR_IN_PX);
  const lineClampAvailable = (blockSettings.blockDuration - HOUR_IN_PX) / HALF_AN_HOUR_BLOCK_SPAN;

  const shiftLabel = useMemo(() => {
    const start: Date = contract.originalStartBlock
      ? getDateFromBlock(contract.originalStartBlock, timezoneName)
      : blockSettings.startAt;
    const end: Date = contract.originalEndBlock
      ? getDateFromBlock(contract.originalEndBlock + 1, timezoneName)
      : blockSettings.endAt;

    return formatStartEndTime(start, end);
  }, [
    blockSettings.endAt,
    blockSettings.startAt,
    contract.originalEndBlock,
    contract.originalStartBlock,
    timezoneName,
  ]);

  return !isShort ? (
    <ScheduleLabels className="schedule-labels" blockColor={blockColor}>
      <ScheduleName lineClampSize={lineClampAvailable > 1 ? lineClampAvailable : 1} className="schedule-name">
        {scheduleIndex}. {scheduleName || scheduleNameShort}
      </ScheduleName>
      <ScheduleIndex className="schedule-index">{scheduleIndex}</ScheduleIndex>
      <ClientName className="schedule-client-name">{clientName}</ClientName>
      <TimeSpan className="schedule-time">{shiftLabel}</TimeSpan>
    </ScheduleLabels>
  ) : (
    <ScheduleLabels className="schedule-labels short" blockColor={blockColor}>
      <ScheduleShortName className="schedule-name">
        {scheduleIndex}. {scheduleNameShort}
      </ScheduleShortName>
      <ScheduleIndex className="schedule-index">{scheduleIndex}</ScheduleIndex>
      <TimeSpanShort className="schedule-time">{shiftLabel}</TimeSpanShort>
    </ScheduleLabels>
  );
};

export { SchedulesContainerLabels };

const ScheduleLabels = styled.div<{ blockColor: string }>`
  position: absolute;
  left: 1px;
  top: 0;
  padding: 4px 10px;
  width: inherit;

  .schedule-label-overlapped:not(.schedule-focused) & {
    z-index: 2;
  }

  &&& {
    .schedule-label-overlapped.update-overlap-color & {
      display: none;
    }

    .schedule-label-overlapped:not(.schedule-focused) & {
      .schedule-index {
        display: flex;
      }

      .schedule-name,
      .schedule-client-name,
      .schedule-time {
        display: none;
      }
    }
  }

  &.short {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  .schedule-name {
    &:empty,
    &:not(:empty) + .schedule-index {
      display: none;
    }
  }

  :not([data-row-length='1'] &) {
    z-index: 1;

    [data-selected-days='7'] & {
      div:not(.schedule-index) {
        display: none;
      }

      .schedule-index {
        display: flex;
      }
    }
  }
`;

const ScheduleName = styled.div<{ lineClampSize: number }>`
  ${props => lineClamp(props.lineClampSize)}
  font-weight: 800;
`;

const ClientName = styled.div`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  margin-top: 5px;
  font-weight: 600;
`;

const TimeSpan = styled.div`
  margin-top: 5px;
`;

const ScheduleShortName = styled.div`
  font-weight: 700;
`;

const TimeSpanShort = styled.div`
  font-weight: 400;
`;

const ScheduleIndex = styled.div`
  display: flex;
  flex-grow: 1;
  z-index: 1;
  font-weight: 700;
`;
